import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";

const TIPOS = ["Todos os tipos", "Solteiro", "Casal", "Família", "Suíte", "Chalé"];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function Tarifas() {
  const [items, setItems] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nome: "", pousada_id: "", tipo_quarto: "", data_inicio: todayStr(), data_fim: "", valor_diaria: 300,
  });

  const load = async () => {
    const [{ data: t }, { data: p }] = await Promise.all([api.get("/tarifas"), api.get("/pousadas")]);
    setItems(t); setPousadas(p);
  };
  useEffect(() => { load(); }, []);

  const pousadaName = (id) => pousadas.find((x) => x.id === id)?.nome || "—";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.pousada_id || !form.data_inicio || !form.data_fim) {
      return toast.error("Preencha todos os campos obrigatórios");
    }
    try {
      await api.post("/tarifas", {
        ...form,
        tipo_quarto: form.tipo_quarto === "Todos os tipos" ? "" : form.tipo_quarto,
        valor_diaria: Number(form.valor_diaria),
      });
      toast.success("Tarifa cadastrada");
      setOpen(false);
      setForm({ nome: "", pousada_id: "", tipo_quarto: "", data_inicio: todayStr(), data_fim: "", valor_diaria: 300 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar tarifa");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta tarifa?")) return;
    try {
      await api.delete(`/tarifas/${id}`);
      toast.success("Tarifa removida");
      load();
    } catch { toast.error("Erro ao remover"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Preços sazonais"
        title="Tarifas"
        description="Cadastre valores para alta e baixa temporada. Aplicados automaticamente ao criar uma reserva no período."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-tarifa" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                <Plus size={16} className="mr-2" /> Nova tarifa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif text-2xl">Nova tarifa sazonal</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Nome / Descrição</Label>
                  <Input data-testid="input-tarifa-nome" value={form.nome} placeholder="Ex: Alta temporada Natal"
                    onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Pousada</Label>
                    <Select value={form.pousada_id} onValueChange={(v) => setForm({ ...form, pousada_id: v })}>
                      <SelectTrigger data-testid="select-tarifa-pousada"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de quarto</Label>
                    <Select value={form.tipo_quarto || "Todos os tipos"} onValueChange={(v) => setForm({ ...form, tipo_quarto: v })}>
                      <SelectTrigger data-testid="select-tarifa-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Início</Label>
                    <Input type="date" data-testid="input-tarifa-inicio" value={form.data_inicio}
                      onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
                  </div>
                  <div>
                    <Label>Fim</Label>
                    <Input type="date" data-testid="input-tarifa-fim" value={form.data_fim}
                      onChange={(e) => setForm({ ...form, data_fim: e.target.value })} />
                  </div>
                  <div>
                    <Label>Valor/diária (R$)</Label>
                    <Input type="number" min="0" step="0.01" data-testid="input-tarifa-valor" value={form.valor_diaria}
                      onChange={(e) => setForm({ ...form, valor_diaria: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-tarifa" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                    Cadastrar tarifa
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Início</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Fim</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Valor/diária</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} data-testid={`tarifa-row-${t.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2 font-medium text-stone-800">
                    <Tag size={14} className="text-[#D4A652]" />{t.nome}
                  </div>
                </TableCell>
                <TableCell>{pousadaName(t.pousada_id)}</TableCell>
                <TableCell>{t.tipo_quarto || "Todos"}</TableCell>
                <TableCell>{fmtDate(t.data_inicio)}</TableCell>
                <TableCell>{fmtDate(t.data_fim)}</TableCell>
                <TableCell className="font-medium text-[#083A6B]">{brl(t.valor_diaria)}</TableCell>
                <TableCell className="text-right">
                  <button
                    data-testid={`btn-delete-tarifa-${t.id}`}
                    onClick={() => remove(t.id)}
                    className="text-stone-400 hover:text-[#D4A652] p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                  Nenhuma tarifa cadastrada. Crie sua primeira tarifa sazonal.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
